/*
 * Books (draft 10, reworked Oct 30): replaces QuickBooks on Jan 1, 2027. Owners only. An overview like QuickBooks' and
 * Xero's dashboards; transactions to review and categorize (bank files, a linked bank through Plaid, typed-in cash),
 * with splits, receipts and rules; reports (profit and loss, balance sheet, general ledger, vendors, 1099s, by company)
 * over any days; reconcile; accounts, rules and categories; and year end (the accountant's zip, closing the books,
 * journal entries); taxes (where and how the business files, the AI bookkeeper switches, the AI tax savings list).
 * The tabs live in screens/books/.
 */
import { useState } from "react";
import { useBooks } from "../api/books";
import { useSession } from "../app/session";
import { todayIn } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { LoadError, Page, Skeleton } from "../components/parts";
import { Overview, type GoTo } from "./books/Overview";
import { Reconcile } from "./books/Reconcile";
import { Reports, type Report } from "./books/Reports";
import { Setup } from "./books/Setup";
import { Taxes } from "./books/Taxes";
import { NoAccounts, Transactions, type ReviewState } from "./books/Transactions";
import { YearEnd } from "./books/YearEnd";

/** The side menu, like Marketing's (2026-10-30: the row of tabs on top read poorly). */
type Tab = "overview" | "transactions" | "reports" | "taxes" | "reconcile" | "accounts" | "yearend";
const TABS: [Tab, string, IconName][] = [
  ["overview", "Overview", "home"],
  ["transactions", "Transactions", "list"],
  ["reports", "Reports", "insights"],
  ["taxes", "Taxes", "sparkle"],
  ["reconcile", "Reconcile", "check"],
  ["accounts", "Accounts and rules", "sliders"],
  ["yearend", "Year end", "file"],
];

export function Books() {
  const { company } = useSession();
  const books = useBooks(company.id);
  const [tab, setTab] = useState<Tab>("overview");
  const [review, setReview] = useState<ReviewState>("pending");
  const [report, setReport] = useState<Report>("cash");
  const today = todayIn(company.timezone);
  if (books.error) return <LoadError error={books.error} retry={() => void books.refetch()} />;
  const setup = books.data;
  const go = (to: GoTo) => {
    if (to === "review" || to === "transactions") {
      setReview(to === "review" ? "pending" : "posted");
      setTab("transactions");
    } else if (to === "c1099") {
      setReport("c1099");
      setTab("reports");
    } else setTab(to);
  };
  return (
    <Page title="Books" subtitle={`${company.name}, owners only`}>
      <div className="settings-layout">
        <aside className="settings-side section-side">
          <nav className="subnav" aria-label="Books">
            <div className="subnav-group">
              {TABS.map(([v, label, icon]) => (
                <button
                  key={v}
                  type="button"
                  className="subnav-btn"
                  aria-current={tab === v ? "page" : undefined}
                  data-status={tab === v ? "active" : undefined}
                  onClick={() => {
                    setTab(v);
                    window.scrollTo({ top: 0 });
                  }}
                >
                  <Icon name={icon} size={16} />
                  {label}
                </button>
              ))}
            </div>
          </nav>
        </aside>
        <div className="stack settings-main">
          {!setup ? (
            <Skeleton rows={6} />
          ) : tab === "overview" ? (
            <Overview setup={setup} today={today} go={go} />
          ) : tab === "transactions" ? (
            setup.accounts.length === 0 ? (
              <NoAccounts setup={setup} onAdd={() => setTab("accounts")} />
            ) : (
              <Transactions setup={setup} today={today} state={review} setState={setReview} />
            )
          ) : tab === "reports" ? (
            <Reports setup={setup} today={today} report={report} setReport={setReport} />
          ) : tab === "taxes" ? (
            <Taxes today={today} />
          ) : tab === "reconcile" ? (
            <Reconcile setup={setup} />
          ) : tab === "accounts" ? (
            <Setup setup={setup} />
          ) : (
            <YearEnd setup={setup} today={today} />
          )}
        </div>
      </div>
    </Page>
  );
}
